/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2025. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2025. Todos los derechos reservados.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         7/05/25 - 11:05:09
 * Module name:  overview-stats.store
 * File name:    overview-stats.store.ts
 * IDE:          WebStorm
 */


import {StatsService} from '@shared/services/settings/stats.service';
import {
  CustomerDeviceCommStatsInterface,
  CustomerStatsInterface,
  StatsDataItemInterface,
} from '@shared/interface/main/overview/overview-stats.interface';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {computed, inject} from '@angular/core';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {switchMap, tap} from 'rxjs';
import {signalResponse} from '@core/parsers/signal-response';
import {CustomerParentsListPayload} from '@shared/interface';
import {PieDataItemOption} from 'echarts/types/src/chart/pie/PieSeries';
import { TranslateService } from '@ngx-translate/core';
import { MkadToastService } from '@core/services/mkad-toast.service';
import {graphic} from 'echarts';

export interface OverviewStatsStoreInterface {
  stats: CustomerStatsInterface | null;
  basicStats: StatsDataItemInterface[];
  pieStats: PieDataItemOption[];
  loading: boolean;
  totalDevices: number;
  successCommPercent: number;
  errorCommPercent: number;
}

export const OverviewStatsInitState: OverviewStatsStoreInterface = {
  stats: null,
  loading: false,
  basicStats: [],
  pieStats: [],
  totalDevices: 0,
  successCommPercent: 0,
  errorCommPercent: 0,
};

const HUNDRED = 100;

export const OverviewStatsStore = signalStore(
  {providedIn: 'root'},
  withState(OverviewStatsInitState),
  withComputed((store) => ({
    data: computed(() => store.stats()),
    basics: computed(() => store.basicStats()),
    pie: computed(() => store.pieStats()),
  })),
  withMethods((
    store,
    statsService = inject(StatsService),
    translate = inject(TranslateService),
    toast = inject(MkadToastService),
  ) => {

    const labelSeries: Record<keyof CustomerDeviceCommStatsInterface, string> = {
      'success': 'app.main.stats.devices.comm.success',
      'warning': 'app.main.stats.devices.comm.error',
      'error': 'app.main.stats.devices.comm.error',
      'total': 'app.main.stats.devices.comm.success',
    };

    const getPercentage = (partialValue: number, totalValue: number) => {
      return (HUNDRED * partialValue) / totalValue;
    }

    function handleStatsResponse(): void {
      const statsData = store.stats();
      if (statsData) {
        const basicStats: StatsDataItemInterface[] = [];
        if (statsData.partners) {
          basicStats.push({
            total: statsData.partners.total,
            enabled: statsData.partners.enabled,
            icon: 'users',
            label: 'app.main.stats.partners.label',
          });
        }
        if (statsData.customers) {
          basicStats.push({
            total: statsData.customers.total,
            enabled: statsData.customers.enabled,
            icon: 'layer-group',
            label: 'app.main.stats.customers.label',
          });
        }
        if (statsData.users) {
          basicStats.push({
            total: statsData.users.total,
            enabled: statsData.users.enabled,
            icon: 'user',
            label: 'app.main.stats.users.label',
          });
        }
        if (statsData.sensors) {
          basicStats.push({
            total: statsData.sensors.total,
            enabled: statsData.sensors.enabled,
            icon: 'signal-stream',
            label: 'app.main.stats.sensors.label',
          });
        }

        const pieStats: PieDataItemOption[] = [];
        let totalDevices = 0;
        let successCommPercent = 0;
        let errorCommPercent = 0;
        if (statsData.devices && statsData.devices.comm) {
          pieStats.push({
            name: translate.instant(labelSeries['success']),
            value: statsData.devices.comm.success,
          });
          pieStats.push({
            name: translate.instant(labelSeries['error']),
            value: statsData.devices.comm.error,
            itemStyle: {
              color: new graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: 'rgba(230,109,149,0.41)'
                },
                {
                  offset: 1,
                  color: 'rgb(255, 70, 131)'
                }]),
            }
          });
          if (pieStats.length >= 2) {
            pieStats[0].itemStyle = {
              color: new graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: 'rgba(135,228,96,0.55)'
                },
                {
                  offset: 1,
                  color: 'rgba(8,214,111,0.73)',
                }]),
            }
            const lastItemIndex = pieStats.length - 1;
            pieStats[lastItemIndex].itemStyle = {
              color: new graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: 'rgba(229,72,99,0.54)'
                },
                {
                  offset: 1,
                  color: 'rgb(229,72,99)',
                }]),
            }
          } else if (pieStats.length > 1) {
            pieStats[0].itemStyle = {
              color: 'rgba(113,218,71,0.82)',
            }
          }
          totalDevices = statsData.devices.comm.total;
          successCommPercent = getPercentage(statsData.devices.comm.success, statsData.devices.comm.total);
          errorCommPercent = getPercentage(statsData.devices.comm.error, statsData.devices.comm.total);
        }
        patchState(store, {basicStats, pieStats, totalDevices, successCommPercent, errorCommPercent});
      }

    }

    const getStats = rxMethod<CustomerParentsListPayload | undefined>(($) => $.pipe(
      tap(() => patchState(store, {loading: true})),
      switchMap((param) => statsService.gstStats(param).pipe(
          signalResponse(
            (response: CustomerStatsInterface) => {
              patchState(store, {loading: false, stats: response});
              handleStatsResponse();
            },
            (error) => {
              patchState(store, {loading: false});
              toast.showError(error.message);
            },
          ),
        ),
      ),
    ));

    return {
      getStats,
    };
  }),
);

export type OverviewStatsStoreType = InstanceType<typeof OverviewStatsStore>;
